import React from "react";
import { FiInfo, FiMail, FiSmartphone } from "react-icons/fi";
import { Card, CardLabel } from "./ui/Card";
import { Num, Pct } from "./ui/Num";
import { Bar } from "./ui/Bar";
import { EMAIL_FUNNEL_STAGES, PUSH_FUNNEL_STAGES } from "./broadcastEnums";

/**
 * The per-channel delivery funnel on the broadcast detail screen.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS COMPONENT EXISTS TO HONOUR: NULL IS NOT ZERO
 * ---------------------------------------------------------------------------
 * A push-only broadcast decodes `email: null`. A `null` channel object means
 * "this channel was NEVER TARGETED" — not "we targeted it and reached nobody".
 *
 * Rendering a row of zeros for an untargeted channel tells the reader a story
 * that is not merely imprecise, it is the opposite of the truth: it reads as a
 * total delivery failure on a channel the author deliberately did not use.
 * So `stages == null` renders NOTHING AT ALL — `return null`, no heading, no
 * empty frame, no "not sent" placeholder that could be misread as a result.
 *
 * (Within a channel that WAS targeted, an individual `null` stage value is a
 * different thing again — the server has no figure yet — and `Num` renders it
 * as an em dash. Absence and zero stay distinguishable at every level.)
 * ---------------------------------------------------------------------------
 *
 * Purely presentational. `stages` is the already-decoded per-channel object;
 * nothing here fetches, and nothing here imports a service.
 */

/* ── Channel identity ─────────────────────────────────────────────────── */

/**
 * Heading, icon and stage list per channel.
 *
 * Icons are real `react-icons/fi` glyphs, not emoji: an emoji is a text
 * character a screen reader will announce ("mobile phone with rightwards
 * arrow"), and it renders differently on every platform.
 * @type {Record<string, {label: string, Icon: React.ElementType, stages: Array}>}
 */
const CHANNEL_META = {
  push: { label: "Push", Icon: FiSmartphone, stages: PUSH_FUNNEL_STAGES },
  email: { label: "Email", Icon: FiMail, stages: EMAIL_FUNNEL_STAGES },
};

/**
 * Which stage each stage is a percentage OF — "the previous MEANINGFUL stage",
 * which is not always the previous stage in the list.
 *
 * Two deliberate departures from strict list order:
 *
 * - `clicked` is measured against `delivered`, not `opened`. Opens are
 *   inflated by machine fetches (see the caveat below); dividing by them would
 *   push an unknown amount of noise into the one engagement number we can
 *   actually stand behind.
 * - `suppressed` and `bounced` sit at the end of the email list but describe
 *   what happened EARLY: a suppressed address never left the queue, a bounce
 *   is a send that failed. Measuring either against `clicked` would be
 *   arithmetic with no meaning attached.
 *
 * `queued` is absent: it is the first stage and has no denominator.
 * @type {Record<string, string>}
 */
const DENOMINATOR_STAGE = {
  sent: "queued",
  delivered: "sent",
  opened: "delivered",
  clicked: "delivered",
  suppressed: "queued",
  bounced: "sent",
};

/** The stage every bar is drawn as a fraction of. */
const FIRST_STAGE = "queued";

/** Stage whose number is a soft signal rather than a fact. */
const CAVEAT_STAGE = "opened";

/* ── Classes ──────────────────────────────────────────────────────────── */

const GRID = "mt-4 grid grid-cols-2 gap-x-5 gap-y-5 md:grid-cols-5";
const VALUE = "text-2xl";
const SUBLINE = "mt-1 text-xs text-bc-soft";

const CELL_TESTID = "bc-funnel-cell";
const BAR_TESTID = "bc-funnel-bar";
const INFO_TESTID = "bc-funnel-opens-info";

/* ── Helpers ──────────────────────────────────────────────────────────── */

/** Is this a real, finite number? `0` is; `null` and `NaN` are not. */
const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * A denominator `Pct` can trust.
 *
 * `Pct` treats a `null`/`undefined` `of` as "value is already a percentage",
 * which would print `8,104` as `8104%`. Collapsing an absent denominator to
 * `0` routes it down `Pct`'s own empty-denominator path, which renders the
 * em dash — the same glyph absence gets everywhere else in the console.
 *
 * @param {unknown} value
 * @returns {number}
 */
const denominatorOf = (value) => (isNumber(value) ? value : 0);

/**
 * A stage's share of the FIRST stage, for the bar width.
 *
 * Returns `null` — an empty track, not a full one — whenever either end of the
 * ratio is missing, because a full bar would assert 100%.
 *
 * @param {unknown} value - This stage's count.
 * @param {unknown} first - The `queued` count.
 * @returns {number|null}
 */
const barPct = (value, first) => {
  if (!isNumber(value) || !isNumber(first) || first <= 0) return null;
  return (value / first) * 100;
};

/* ── Cells ────────────────────────────────────────────────────────────── */

/**
 * The opens caveat, as an inline ⓘ plus the two things assistive tech needs.
 *
 * Email opens are counted by a tracking pixel, and Gmail's image proxy and
 * Apple Mail Privacy Protection both fetch that pixel with no human anywhere
 * near the message. The number is therefore inflated by an unknown, unknowable
 * amount. `delivered`, `bounced` and `clicked` are facts; `opened` is a soft
 * signal, and the UI has to say so at the point of reading rather than in a
 * footnote nobody scrolls to.
 *
 * This is also exactly why there is no open-rate KPI in the detail header:
 * a headline figure is read as a fact, and this one is not one.
 *
 * The text is carried by a visually-hidden span referenced through
 * `aria-describedby`, because a `title` alone is invisible to most screen
 * readers and unreachable by keyboard.
 *
 * @param {Object} props
 * @param {string} props.noteId - Id of the visually-hidden span carrying the note.
 * @param {string} props.note - The API's `notes.opens` caveat text.
 * @returns {React.ReactElement}
 */
const OpensCaveat = ({ noteId, note }) => (
  <>
    <FiInfo
      data-testid={INFO_TESTID}
      aria-hidden="true"
      className="ml-1 inline-block h-3 w-3 shrink-0 align-[-1px] text-bc-faint"
    />
    <span id={noteId} className="sr-only">
      {note}
    </span>
  </>
);

/**
 * One stage of the funnel: label, value, sub-caption, bar.
 *
 * ACCESSIBILITY: `Bar` is `aria-hidden` by construction, so the value is
 * ALWAYS present as adjacent text via `Num`. No stage's meaning is ever
 * carried by the bar's colour or length alone — strip the CSS and every
 * number in this grid is still readable.
 *
 * @param {Object} props
 * @param {{id: string, label: string}} props.stage - Stage descriptor from the enums.
 * @param {number|null} props.value - This stage's count.
 * @param {number|null} props.first - The `queued` count, for the bar.
 * @param {{id: string, label: string}} [props.denominator] - Previous meaningful stage.
 * @param {number|null} [props.denominatorValue] - That stage's count.
 * @param {React.ReactNode} [props.caption] - Sub-caption for the first stage, which has no denominator.
 * @param {'push'|'email'} props.channel - Drives the bar tone.
 * @param {string} [props.note] - Opens caveat; only the opened cell receives it.
 * @param {string} props.noteId - Id for the caveat's visually-hidden span.
 * @returns {React.ReactElement}
 */
const StageCell = ({
  stage,
  value,
  first,
  denominator,
  denominatorValue,
  caption,
  channel,
  note,
  noteId,
}) => {
  const hasCaveat = stage.id === CAVEAT_STAGE && Boolean(note);

  return (
    <div
      data-testid={CELL_TESTID}
      data-stage={stage.id}
      title={hasCaveat ? note : undefined}
      aria-describedby={hasCaveat ? noteId : undefined}
    >
      <CardLabel>
        {stage.label}
        {hasCaveat ? <OpensCaveat noteId={noteId} note={note} /> : null}
      </CardLabel>

      <div className="mt-1.5">
        <Num value={value} className={VALUE} emphasis />
      </div>

      <div className={SUBLINE}>
        {denominator ? (
          <>
            <Pct
              value={value}
              of={denominatorOf(denominatorValue)}
              className="text-xs"
            />{" "}
            of {denominator.label.toLowerCase()}
          </>
        ) : (
          caption
        )}
      </div>

      <div data-testid={BAR_TESTID} className="mt-2">
        <Bar pct={barPct(value, first)} tone={channel} />
      </div>
    </div>
  );
};

/* ── Component ────────────────────────────────────────────────────────── */

/**
 * The delivery funnel for one channel of one broadcast.
 *
 * @param {Object} props
 * @param {'push'|'email'} props.channel - Which channel. Drives the heading, the icon and the bar tone.
 * @param {Object|null} props.stages - Decoded per-channel counts: push `{queued, sent, delivered, opened, clicked}`, email the same plus `{suppressed, bounced}`. Any value may be null. **`null` means the channel was never targeted and renders nothing.**
 * @param {string|null} [props.note] - The API's `notes.opens` caveat, surfaced on the opened cell only.
 * @param {string} [props.segment] - Segment label for the sub-caption and the heading row.
 * @param {string} [props.className] - Extra classes on the card, merged last.
 * @returns {React.ReactElement|null}
 */
export const ChannelFunnel = ({
  channel,
  stages,
  note,
  segment,
  className = "",
}) => {
  // See the file header. An untargeted channel renders NOTHING — not a frame,
  // not a placeholder, and above all not a row of zeros.
  if (!stages) return null;

  const meta = CHANNEL_META[channel] || CHANNEL_META.push;
  const { Icon } = meta;
  const first = stages[FIRST_STAGE];
  const noteId = `bc-funnel-${channel}-opens-note`;

  return (
    <Card as="section" className={`p-5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        {/*
          h2, not h3. This is a top-level section of the detail page, whose
          only other heading is the broadcast title at h1 — and `CardHeader`
          renders its title as a plain div, so no card contributes a heading
          in between. At h3 the page read h1 → h3 and a screen-reader user
          navigating by heading skipped a level at every funnel.
        */}
        <h2 className="flex items-center gap-2 text-base font-semibold text-bc-text">
          <Icon aria-hidden="true" className="h-4 w-4 text-bc-muted" />
          {meta.label}
        </h2>
        {segment ? (
          <div className="shrink-0 text-xs text-bc-soft">{segment}</div>
        ) : null}
      </div>

      <div className={GRID}>
        {meta.stages.map((stage) => {
          const denominatorId = DENOMINATOR_STAGE[stage.id];
          const denominator = denominatorId
            ? meta.stages.find((item) => item.id === denominatorId)
            : null;

          return (
            <StageCell
              key={stage.id}
              stage={stage}
              value={stages[stage.id]}
              first={first}
              denominator={denominator}
              denominatorValue={denominatorId ? stages[denominatorId] : null}
              // The first stage has no denominator, so it names the audience
              // the funnel starts from instead.
              caption={denominatorId ? null : segment}
              channel={channel}
              note={note}
              noteId={noteId}
            />
          );
        })}
      </div>
    </Card>
  );
};

export default ChannelFunnel;
